import React from "react";
import { TAG_DISPLAY } from "common/constants";
import { spacing } from "../../../../../../../../stitches.config";
import { ActionButton } from "../../../../../../../Common/Buttons/ActionButton";
import { Button } from "../../../../../../../Common/Buttons/Button";
import TextInput from "../../../../../../../Inputs/Text";
import {
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../../form/form";
import {
  ActionButtonContainer,
  InputContainer,
  LabelContainer,
  LVLabel,
  OptionsContainer,
} from "../__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "../FieldSettingsView";
import { useCreateEditOptionModal } from "./CreateEditOptionModal";
import type { DropdownInputType, DropdownOption, OneOfOptions } from "./types";
import {
  getSchemaOptionsPath,
  useEnsureValidDefaultValue,
  useSetOptions,
} from "./utils";

export function OptionsList({
  fieldPath,
  isReadOnly,
  isModuleInput,
  inputType,
}: InputSettingsFormProps & {
  inputType: DropdownInputType;
}) {
  const { setValue } = useBuilderFormContext();

  const useAsTag: boolean | undefined = useBuilderWatch(
    getUiSchemaPropertyFormPath(fieldPath, "useAsTag"),
  );

  const options: OneOfOptions = useBuilderWatch(
    getSchemaOptionsPath(fieldPath, inputType),
  );

  const formattedOptions =
    options && options.length ?
      options.map(option => {
        if (typeof option === "string") {
          return {
            const: option,
            title: option,
            ...(useAsTag ? { display: TAG_DISPLAY.NEUTRAL } : {}),
          };
        }

        return option;
      })
    : [{ const: "", title: "" }];

  const setOneOfValue = useSetOptions(fieldPath, inputType);
  const ensureValidDefaultValue = useEnsureValidDefaultValue({
    fieldPath,
    inputType,
    setValue,
  });

  function deleteOption(index: number) {
    const newOptions = [...formattedOptions];
    newOptions.splice(index, 1);

    ensureValidDefaultValue(newOptions);
    setOneOfValue(newOptions);
  }

  function updateOption(index: number, option: DropdownOption) {
    const newOptions = [...formattedOptions];
    newOptions[index] = option;

    ensureValidDefaultValue(newOptions);
    setOneOfValue(newOptions);
  }

  function addOption(option: DropdownOption) {
    setOneOfValue([...formattedOptions, option]);
  }

  const [showCreateEditDropdownOptionModal] = useCreateEditOptionModal();

  return (
    <div>
      <div>
        <LabelContainer>
          <LVLabel text="Label" />
          <LVLabel text="Value" />
        </LabelContainer>
        {formattedOptions.map((option, index) => {
          return (
            <OptionsContainer key={`${option.const}-${index}`}>
              <InputContainer>
                <TextInput
                  name={`label.${index}`}
                  value={option.title}
                  disabled
                />
              </InputContainer>
              <TextInput
                name={`value.${index}`}
                value={String(option.const)}
                disabled
              />
              {!isReadOnly && (
                <ActionButtonContainer>
                  <ActionButton
                    icon="pencil"
                    data-testid={`dropdown-option-edit-${option.const}`}
                    onClick={() => {
                      showCreateEditDropdownOptionModal({
                        useAsTag,
                        existingOption: option,
                        // the existence of an onDelete function is what
                        // determines whether a delete button is shown
                        onDelete:
                          formattedOptions.length > 1 && !isModuleInput ?
                            () => deleteOption(index)
                          : undefined,
                        onSave: (option: DropdownOption) => {
                          updateOption(index, option);
                        },
                        valueEditingDisabled: isModuleInput,
                      });
                    }}
                  />
                </ActionButtonContainer>
              )}
            </OptionsContainer>
          );
        })}
      </div>
      <div style={{ paddingTop: spacing.xs.value }}>
        <Button
          title="Add option"
          variant="outline"
          size="small"
          iconStart="plus"
          onClick={() =>
            showCreateEditDropdownOptionModal({
              useAsTag,
              onSave: addOption,
              // if you can create the option, you should be allowed to edit the
              // value
              valueEditingDisabled: false,
            })
          }
          isDisabled={isModuleInput || isReadOnly}
        />
      </div>
    </div>
  );
}
